import { useEffect, useMemo, useRef, useState } from 'react';
import { WIN_CONDITIONS, deckLabel, type WinCondition, winConditionLabel } from '@edh/shared';
import { navigate } from '../lib/router';
import { useStore } from '../lib/store';

/**
 * The fifteen-second path, for a game that was played without the app open.
 *
 * Standard play records turn count and final life on its own, so this is the
 * fallback rather than the main route — but it is the one the spec is built
 * around, and a night where nobody remembered to start the counter should still
 * be loggable while shuffling for the next game.
 */

const SEATS_KEY = 'edh.lastSeats';

interface Seat {
  pilotId: string;
  deckId: string;
}

/** A date input's value: the local day, not a UTC instant. */
const localDay = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/**
 * Midday local on the chosen day.
 *
 * Midday rather than midnight so that no timezone offset can push a game onto
 * the day before or after — the app groups games into nights by local date, and
 * a game that lands an hour the wrong side of midnight becomes its own night.
 */
function timestampFor(day: string): number | undefined {
  const [y, m, d] = day.split('-').map(Number);
  if (!y || !m || !d) return undefined;
  return new Date(y, m - 1, d, 12, 0, 0).getTime();
}

/** 1st, 2nd, 3rd, 4th — the only four a pod ever needs. */
const ordinal = (n: number): string => ['—', '1st', '2nd', '3rd', '4th'][n] ?? `${n}th`;

export function LogPastGame({ onDone }: { onDone: () => void }) {
  const { state, logMatch } = useStore();
  const pilots = useMemo(() => state.pilots.filter((p) => !p.retired), [state.pilots]);
  const decks = useMemo(() => state.decks.filter((d) => !d.retired), [state.decks]);

  const [count, setCount] = useState(2);
  const [seats, setSeats] = useState<Seat[]>([]);
  /**
   * Where each seat finished, by seat index. 1 is the winner.
   *
   * Kept as places rather than just a winner because the difference between
   * second and last is most of what a pod decides, and it is the one thing
   * somebody reconstructing a game from memory reliably remembers. Without it
   * the engine has to treat every loser as having drawn with every other loser
   * — honest for a game where nobody wrote the order down, and a waste of what
   * is actually known here.
   */
  const [places, setPlaces] = useState<Record<number, number>>({});
  const [showDetails, setShowDetails] = useState(false);
  const [busy, setBusy] = useState(false);

  /**
   * The night it was played, defaulting to today.
   *
   * A backfilled game landing on today's date would fold into tonight's
   * session — one night's worth of games becoming part of another, with the
   * streaks and the "nights played" counts quietly wrong. Dated as a local day
   * at midday, so a timezone cannot nudge it either side of midnight.
   */
  const [playedOn, setPlayedOn] = useState(() => localDay(new Date()));

  // Optional per-game fields — spec 5, tier 1 and 2.
  const [endTurn, setEndTurn] = useState('');
  const [onThePlay, setOnThePlay] = useState('');
  const [winCondition, setWinCondition] = useState<WinCondition | ''>('');
  const [note, setNote] = useState('');
  // Feeds the margin-of-victory multiplier, so it sits on the main flow rather
  // than behind Details — a rating input nobody can see is a rating input
  // nobody fills in.
  const [winnerLife, setWinnerLife] = useState('');

  // The same people usually sit down in the same order, so start where we left off.
  // Keyed on the seat count alone: a background sync landing mid-entry must not
  // reset the selections of someone halfway through logging a game.
  const pilotsRef = useRef(pilots);
  pilotsRef.current = pilots;

  useEffect(() => {
    const saved = localStorage.getItem(SEATS_KEY);
    const previous: Seat[] = saved ? JSON.parse(saved) : [];
    setSeats((prev) =>
      Array.from({ length: count }, (_, i) => ({
        // Anything already chosen for this seat stays chosen.
        pilotId: prev[i]?.pilotId || previous[i]?.pilotId || pilotsRef.current[i]?.id || '',
        // Decks are not restored across games: reusing last night's deck is
        // exactly the habit the rating system is trying to price.
        deckId: prev[i]?.deckId ?? '',
      })),
    );
  }, [count]);

  const setSeat = (i: number, patch: Partial<Seat>) =>
    setSeats((prev) => prev.map((s, j) => (j === i ? { ...s, ...patch } : s)));

  const usedDecks = new Set(seats.map((s) => s.deckId).filter(Boolean));
  const usedPilots = new Set(seats.map((s) => s.pilotId).filter(Boolean));

  /** Whoever was given first. Everything downstream reads the places. */
  const winnerSeat = seats.findIndex((_, i) => places[i] === 1);
  const winner = winnerSeat >= 0 ? (seats[winnerSeat]?.pilotId ?? null) : null;

  /**
   * The full order, or nothing at all.
   *
   * Half an order is worse than none: the engine refuses a standing that does
   * not name everybody, and quietly dropping it would score the game one way
   * while the screen showed another. So it is all-or-nothing, and a game with
   * only a winner marked falls back to what the league has always done for a
   * game nobody wrote the order down for.
   */
  const ordered = seats.length > 0 && seats.every((_, i) => places[i] !== undefined);
  const standing =
    ordered && winner
      ? Object.fromEntries(seats.map((s, i) => [s.pilotId, places[i]!]))
      : undefined;

  const ready =
    seats.length >= 2 &&
    seats.every((s) => s.pilotId && s.deckId) &&
    usedPilots.size === seats.length &&
    usedDecks.size === seats.length &&
    // Dropping from four seats to two can strand the winner on a seat that no
    // longer exists, so check membership rather than just non-null.
    winner !== null &&
    seats.some((s) => s.pilotId === winner);

  /**
   * Give a seat a place, taking it off whoever had it.
   *
   * A straight swap rather than a shuffle: places are unique by construction,
   * so there is never a half-valid order on screen and never a standing the
   * engine would refuse.
   */
  function setPlace(seat: number, place: number) {
    setPlaces((prev) => {
      if (prev[seat] === place) {
        const { [seat]: _cleared, ...rest } = prev;
        return rest;
      }
      const next: Record<number, number> = {};
      for (const [key, value] of Object.entries(prev)) {
        const other = Number(key);
        if (other === seat) continue;
        // Whoever held this place trades for the one being vacated, so two taps
        // reorder a pod rather than leaving a gap to tidy up.
        next[other] = value === place && prev[seat] !== undefined ? prev[seat]! : value;
        if (value === place && prev[seat] === undefined) delete next[other];
      }
      next[seat] = place;
      return next;
    });
  }

  function randomiseDecks() {
    const pool = [...decks].sort(() => Math.random() - 0.5);
    setSeats((prev) => prev.map((s, i) => ({ ...s, deckId: pool[i]?.id ?? s.deckId })));
  }

  async function submit() {
    if (!ready || busy) return;
    setBusy(true);
    try {
      localStorage.setItem(SEATS_KEY, JSON.stringify(seats));
      await logMatch({
        entries: seats,
        winnerPilotId: winner!,
        // The night it happened, so a backfilled game does not fold into
        // tonight's session and skew the streaks.
        ts: timestampFor(playedOn),
        // Second place is worth more than last, and this is the only record of
        // which was which. Omitted entirely unless every seat has a place.
        standing,
        endTurn: endTurn ? Number(endTurn) : undefined,
        onThePlay: onThePlay || undefined,
        winCondition: winCondition || undefined,
        note: note || undefined,
        // Left blank means "not measured", which keeps the multiplier neutral.
        // Sending 0 instead would read as a blowout against the winner.
        lifeRemaining: winnerLife === '' ? undefined : { [winner!]: Number(winnerLife) },
        /*
         * Nothing else is sent, on purpose.
         *
         * No damage log, no poison, no commander damage, no duration. A game
         * reconstructed from memory has none of that, and a zero is not the
         * same as an absence — the screens that read these treat a missing one
         * as "not measured" and skip it, where a zero would be a claim that
         * nobody took any damage all game.
         */
      });
      setPlaces({});
      setEndTurn('');
      setOnThePlay('');
      setWinCondition('');
      setNote('');
      setWinnerLife('');
      setSeats((prev) => prev.map((s) => ({ ...s, deckId: '' })));
      setShowDetails(false);
      onDone();
    } finally {
      setBusy(false);
    }
  }

  if (pilots.length < 2 || decks.length < 2) {
    return (
      <div className="empty">
        <strong>Two guildmates and two decks first</strong>
        You need at least a pair of each before there is a game to record.
        <div style={{ marginTop: 18, display: 'flex', gap: 8, justifyContent: 'center' }}>
          <button className="btn" onClick={() => navigate({ name: 'guildmates' })}>
            Add guildmates
          </button>
          <button className="btn" onClick={() => navigate({ name: 'decks' })}>
            Add decks
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="section-head">
        {/* How many are in the pod, not who — "at the table" says that without
            borrowing a term that means something else on the other screens. */}
        <h2>At the table</h2>
        <div className="chips">
          {[2, 3, 4].map((n) => (
            <button key={n} className="chip" aria-pressed={count === n} onClick={() => setCount(n)}>
              {n}
            </button>
          ))}
        </div>
      </div>

      {seats.map((seat, i) => {
        const isWinner = places[i] === 1 && seat.pilotId !== '';
        return (
          <div key={i} className={`seat${isWinner ? ' winner' : ''}`}>
            <div className="seat-head">
              <span className="seat-label">Seat {i + 1}</span>
              {/* Where they finished, not just whether they won. Second place
                  is worth more than last and this is the only record of which
                  was which — and for a game being reconstructed from memory it
                  is the part people actually remember. */}
              <div className="chips">
                {seats.map((_, place) => (
                  <button
                    key={place}
                    className="chip"
                    aria-pressed={places[i] === place + 1}
                    aria-label={`${ordinal(place + 1)} place`}
                    disabled={!seat.pilotId}
                    onClick={() => setPlace(i, place + 1)}
                  >
                    {place === 0 && places[i] === 1 ? '★' : ordinal(place + 1)}
                  </button>
                ))}
              </div>
            </div>

            <select
              className="select"
              style={{ marginBottom: 8 }}
              value={seat.pilotId}
              onChange={(e) => setSeat(i, { pilotId: e.target.value })}
            >
              <option value="">Guildmate…</option>
              {pilots.map((p) => (
                <option key={p.id} value={p.id} disabled={usedPilots.has(p.id) && p.id !== seat.pilotId}>
                  {p.name}
                </option>
              ))}
            </select>

            <select
              className="select"
              value={seat.deckId}
              onChange={(e) => setSeat(i, { deckId: e.target.value })}
            >
              <option value="">Deck…</option>
              {decks.map((d) => (
                <option key={d.id} value={d.id} disabled={usedDecks.has(d.id) && d.id !== seat.deckId}>
                  {deckLabel(d)}
                </option>
              ))}
            </select>
          </div>
        );
      })}

      {/* When it was played. A backfilled game dated today would fold into
          tonight's session, and the nights and streaks would quietly count it
          as part of a night it had nothing to do with. */}
      <div className="panel" style={{ marginBottom: 10 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ flex: 1, fontSize: 14 }}>
            Played on
            <span className="hint" style={{ display: 'block' }}>
              Today unless you say otherwise.
            </span>
          </span>
          <input
            className="input"
            type="date"
            max={localDay(new Date())}
            style={{ width: 160 }}
            value={playedOn}
            onChange={(e) => setPlayedOn(e.target.value)}
          />
        </label>
      </div>

      {winner && (
        <div className="panel" style={{ marginBottom: 10 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ flex: 1, fontSize: 14 }}>
              Winner’s life left
              <span className="hint" style={{ display: 'block' }}>
                Optional, and safe to leave blank — an unrecorded life total
                leaves the margin of victory neutral rather than counting as a
                narrow win.
              </span>
            </span>
            <input
              className="input num"
              inputMode="numeric"
              placeholder="—"
              maxLength={3}
              style={{ width: 76, textAlign: 'center', fontSize: 18 }}
              value={winnerLife}
              onChange={(e) => setWinnerLife(e.target.value.replace(/\D/g, '').slice(0, 3))}
            />
          </label>
        </div>
      )}

      <div className="btn-row" style={{ marginTop: 4 }}>
        <button className="btn ghost" onClick={randomiseDecks}>
          Random decks
        </button>
        <button className="btn ghost" onClick={() => setShowDetails((v) => !v)}>
          {showDetails ? 'Hide details' : 'Details'}
        </button>
      </div>

      {showDetails && (
        <div className="panel" style={{ marginTop: 12 }}>
          <label className="field">
            <span>Turn the game ended</span>
            <input
              className="input"
              inputMode="numeric"
              placeholder="e.g. 9"
              value={endTurn}
              onChange={(e) => setEndTurn(e.target.value.replace(/\D/g, ''))}
            />
          </label>

          <label className="field">
            <span>On the play</span>
            <select className="select" value={onThePlay} onChange={(e) => setOnThePlay(e.target.value)}>
              <option value="">—</option>
              {seats
                .filter((s) => s.pilotId)
                .map((s) => (
                  <option key={s.pilotId} value={s.pilotId}>
                    {pilots.find((p) => p.id === s.pilotId)?.name}
                  </option>
                ))}
            </select>
          </label>

          <label className="field">
            <span>How it ended</span>
            <select
              className="select"
              value={winCondition}
              onChange={(e) => setWinCondition(e.target.value as WinCondition | '')}
            >
              <option value="">—</option>
              {WIN_CONDITIONS.map((value) => (
                <option key={value} value={value}>
                  {winConditionLabel(value)}
                </option>
              ))}
            </select>
          </label>

          <label className="field" style={{ marginBottom: 0 }}>
            <span>Note</span>
            <input
              className="input"
              placeholder="lost to mana screw"
              maxLength={140}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </label>
        </div>
      )}

      <button
        className="btn primary block"
        style={{ marginTop: 16, padding: '15px 16px', fontSize: 17 }}
        disabled={!ready || busy}
        onClick={() => void submit()}
      >
        {busy ? <span className="spinner" /> : 'Log the game'}
      </button>

      <button className="btn ghost block" style={{ marginTop: 10 }} onClick={onDone}>
        Cancel
      </button>
    </>
  );
}
